/** Tipos e helpers para mensagens interactive WhatsApp (button / list / enquete / carrossel). */

export type WhatsAppInteractiveType = "button" | "list";

export type FabInteractiveKind = "button" | "list" | "poll" | "carousel";

export const FAB_INTERACTIVE_METADATA_KEY = "fab_interactive";

export interface FabInteractiveButton {
  id: string;
  title: string;
}

export interface FabInteractiveListRow {
  id: string;
  title: string;
  description?: string;
  section?: string;
}

export interface FabInteractivePoll {
  question: string;
  options: string[];
  allowMultiple: boolean;
}

export interface FabInteractiveCarouselCard {
  text: string;
  image?: string;
  buttons: { id: string; text: string; type?: string }[];
}

/** Modelo canônico provider-agnóstico para UI e metadata. */
export interface FabInteractiveCanonical {
  kind: FabInteractiveKind;
  bodyText: string;
  footerText?: string;
  buttons?: FabInteractiveButton[];
  menuButton?: string;
  listRows?: FabInteractiveListRow[];
  poll?: FabInteractivePoll;
  carousel?: { cards: FabInteractiveCarouselCard[] };
}

export interface FabInteractiveInboundReply {
  kind: "button_reply" | "list_reply" | "poll_vote";
  reply: {
    kind: "button" | "list" | "poll";
    title: string;
    id?: string;
    description?: string;
    selectedOptions?: string[];
  };
}

/** Enquete enviada via lista interativa (Cloud API não tem tipo poll nativo). */
export interface FabPollMetadata {
  question: string;
  options: string[];
  /** Sempre false na Cloud API — lista permite só uma resposta. */
  allowMultiple: boolean;
  mappedTo: "list";
}

export interface WhatsAppPollDisplay {
  question: string;
  options: { id: string; title: string }[];
  allowMultiple: boolean;
}

export const FAB_POLL_MENU_BUTTON = "Votar";
export const FAB_POLL_METADATA_KEY = "fab_poll";

export interface WhatsAppInteractiveButton {
  id: string;
  title: string;
}

export interface WhatsAppInteractiveListRow {
  id: string;
  title: string;
  description?: string;
}

export interface WhatsAppInteractiveDisplay {
  type: WhatsAppInteractiveType;
  bodyText: string;
  buttons?: WhatsAppInteractiveButton[];
  menuButton?: string;
  listRows?: WhatsAppInteractiveListRow[];
}

export interface WhatsAppCarouselCardDisplay {
  text: string;
  imageUrl?: string;
  buttons: { id: string; text: string; type?: string }[];
}

export interface WhatsAppCarouselDisplay {
  bodyText: string;
  cards: WhatsAppCarouselCardDisplay[];
}

const CAROUSEL_DEFAULT_BUTTON = "Ver mais";

export interface WhatsAppInteractiveInboundReply {
  title: string;
  description?: string;
  kind: "button" | "list";
  id?: string;
}

const PREVIEW_SUFFIX_RE =
  /\n\[(Botões|Menu|Opções):[^\]]*\](\n\[(Botões|Menu|Opções):[^\]]*\])?$/;

export function slugifyInteractiveId(
  title: string,
  index: number,
  prefix: "btn" | "row" = "btn",
): string {
  const slug = title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 240);
  const base = slug || `${prefix}_${index + 1}`;
  const withIndex = `${base}_${index + 1}`;
  return withIndex.slice(0, 256);
}

export function stripInteractivePreviewFallback(content: string): string {
  return content.replace(PREVIEW_SUFFIX_RE, "").trimEnd();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

export function parseFabInteractive(
  metadata: Record<string, unknown> | undefined | null,
): FabInteractiveCanonical | null {
  if (!metadata) return null;
  const node = asRecord(metadata[FAB_INTERACTIVE_METADATA_KEY]);
  if (!node) return null;

  const kind = node.kind;
  if (kind !== "button" && kind !== "list" && kind !== "poll" && kind !== "carousel") return null;

  const bodyText = String(node.bodyText ?? "").trim();
  if (!bodyText) return null;

  const footerText = node.footerText ? String(node.footerText).trim() : undefined;

  if (kind === "button") {
    const buttons = Array.isArray(node.buttons)
      ? node.buttons
          .map((b) => {
            const btn = asRecord(b);
            const title = String(btn?.title ?? "").trim();
            const id = String(btn?.id ?? "").trim();
            if (!title) return null;
            return { id: id || title, title };
          })
          .filter(Boolean) as FabInteractiveButton[]
      : [];
    if (buttons.length === 0) return null;
    return { kind, bodyText, footerText, buttons };
  }

  if (kind === "list") {
    const menuButton = String(node.menuButton ?? "").trim() || "Ver opções";
    const listRows = Array.isArray(node.listRows)
      ? node.listRows
          .map((r) => {
            const row = asRecord(r);
            const title = String(row?.title ?? "").trim();
            const id = String(row?.id ?? "").trim();
            if (!title) return null;
            const description = row?.description ? String(row.description).trim() : undefined;
            const section = row?.section ? String(row.section).trim() : undefined;
            return {
              id: id || title,
              title,
              ...(description ? { description } : {}),
              ...(section ? { section } : {}),
            };
          })
          .filter(Boolean) as FabInteractiveListRow[]
      : [];
    if (listRows.length === 0) return null;
    return { kind, bodyText, footerText, menuButton, listRows };
  }

  if (kind === "poll") {
    const pollNode = asRecord(node.poll);
    const question = String(pollNode?.question ?? bodyText).trim();
    const options = Array.isArray(pollNode?.options)
      ? pollNode!.options!.map((o) => String(o ?? "").trim()).filter(Boolean)
      : [];
    if (!question || options.length < 2) return null;
    return {
      kind,
      bodyText: question,
      poll: {
        question,
        options,
        allowMultiple: pollNode?.allowMultiple === true,
      },
    };
  }

  const carouselNode = asRecord(node.carousel);
  const cards = Array.isArray(carouselNode?.cards)
    ? carouselNode!.cards!
        .map((c) => {
          const card = asRecord(c);
          const text = String(card?.text ?? "").trim();
          const buttons = Array.isArray(card?.buttons)
            ? card!.buttons!
                .map((b) => {
                  const btn = asRecord(b);
                  const label = String(btn?.text ?? btn?.title ?? "").trim();
                  const id = String(btn?.id ?? "").trim();
                  if (!label) return null;
                  return { id: id || label, text: label, type: btn?.type ? String(btn.type) : undefined };
                })
                .filter(Boolean) as FabInteractiveCarouselCard["buttons"]
            : [];
          if (!text) return null;
          const imageRaw = card?.image ?? card?.imageUrl;
          const image = imageRaw ? String(imageRaw).trim() : undefined;
          return { text, ...(image ? { image } : {}), buttons };
        })
        .filter(Boolean) as FabInteractiveCarouselCard[]
    : [];
  if (cards.length === 0) return null;
  return { kind, bodyText, carousel: { cards } };
}

export function fabInteractiveToDisplay(
  canonical: FabInteractiveCanonical,
): WhatsAppInteractiveDisplay | WhatsAppPollDisplay | null {
  if (canonical.kind === "poll" && canonical.poll) {
    return {
      question: canonical.poll.question,
      options: canonical.poll.options.map((title, i) => ({
        id: slugifyInteractiveId(title, i, "row"),
        title,
      })),
      allowMultiple: canonical.poll.allowMultiple,
    };
  }
  if (canonical.kind === "button" && canonical.buttons?.length) {
    return { type: "button", bodyText: canonical.bodyText, buttons: canonical.buttons };
  }
  if (canonical.kind === "list" && canonical.listRows?.length) {
    return {
      type: "list",
      bodyText: canonical.bodyText,
      menuButton: canonical.menuButton || "Ver opções",
      listRows: canonical.listRows,
    };
  }
  return null;
}

export function buildFabInteractiveFromButtonForm(bodyText: string, buttons: { title: string }[]): FabInteractiveCanonical {
  const valid = buttons.filter((b) => b.title.trim()).slice(0, 3);
  return {
    kind: "button",
    bodyText: bodyText.trim(),
    buttons: valid.map((b, i) => ({
      id: slugifyInteractiveId(b.title.trim(), i, "btn"),
      title: b.title.trim(),
    })),
  };
}

export function buildFabInteractiveFromListForm(
  bodyText: string,
  menuButton: string,
  rows: { title: string; description?: string; section?: string }[],
): FabInteractiveCanonical {
  const valid = rows.filter((r) => r.title.trim()).slice(0, 10);
  return {
    kind: "list",
    bodyText: bodyText.trim(),
    menuButton: menuButton.trim() || "Ver opções",
    listRows: valid.map((r, i) => ({
      id: slugifyInteractiveId(r.title.trim(), i, "row"),
      title: r.title.trim(),
      ...(r.description?.trim() ? { description: r.description.trim() } : {}),
      ...(r.section?.trim() ? { section: r.section.trim() } : {}),
    })),
  };
}

function normalizeCarouselCardButtons(
  buttons: { text: string; type?: string; id?: string }[],
  cardIndex: number,
): FabInteractiveCarouselCard["buttons"] {
  const valid = buttons
    .filter((b) => b.text.trim())
    .slice(0, 3)
    .map((b, j) => {
      const type = (b.type || "REPLY").toUpperCase();
      const text = b.text.trim();
      const id =
        type === "URL" || type === "COPY" || type === "CALL"
          ? (b.id?.trim() || text)
          : slugifyInteractiveId(b.id?.trim() || text, j, "btn");
      return { id, text, type };
    });

  if (valid.length > 0) return valid;

  return [
    {
      id: slugifyInteractiveId(CAROUSEL_DEFAULT_BUTTON, cardIndex, "btn"),
      text: CAROUSEL_DEFAULT_BUTTON,
      type: "REPLY",
    },
  ];
}

export function buildFabInteractiveFromCarouselForm(
  bodyText: string,
  cards: { text: string; imageUrl?: string; buttons?: { text: string; type?: string; id?: string }[] }[],
): FabInteractiveCanonical {
  const validCards = cards.filter((c) => c.text.trim()).slice(0, 10);
  return {
    kind: "carousel",
    bodyText: bodyText.trim(),
    carousel: {
      cards: validCards.map((card, i) => ({
        text: card.text.trim(),
        ...(card.imageUrl?.trim() ? { image: card.imageUrl.trim() } : {}),
        buttons: normalizeCarouselCardButtons(card.buttons ?? [], i),
      })),
    },
  };
}

export interface ProductCarouselInput {
  productName: string;
  bodyText: string;
  productUrl?: string | null;
  cards: { imageUrl: string; text?: string }[];
}

export function buildFabInteractiveFromProductCarousel(
  input: ProductCarouselInput,
): FabInteractiveCanonical {
  const interestButton = "Tenho interesse";
  return {
    kind: "carousel",
    bodyText: input.bodyText.trim(),
    carousel: {
      cards: input.cards.map((card, i) => {
        const text = card.text?.trim() || input.productName;
        const buttons = input.productUrl?.trim()
          ? [{ id: input.productUrl.trim(), text: "Ver produto", type: "URL" }]
          : normalizeCarouselCardButtons([{ text: interestButton }], i);
        return {
          text,
          image: card.imageUrl,
          buttons,
        };
      }),
    },
  };
}

export function buildFabInteractiveFromPollForm(question: string, options: string[]): FabInteractiveCanonical {
  const valid = options.map((o) => o.trim()).filter(Boolean).slice(0, 10);
  return {
    kind: "poll",
    bodyText: question.trim(),
    poll: {
      question: question.trim(),
      options: valid,
      allowMultiple: false,
    },
  };
}

export function parseFabPoll(
  metadata: Record<string, unknown> | undefined | null,
): FabPollMetadata | null {
  if (!metadata) return null;
  const poll = asRecord(metadata[FAB_POLL_METADATA_KEY]);
  if (!poll) return null;

  const question = String(poll.question ?? "").trim();
  const options = Array.isArray(poll.options)
    ? poll.options.map((o) => String(o ?? "").trim()).filter(Boolean)
    : [];
  if (!question || options.length < 2) return null;

  return {
    question,
    options,
    allowMultiple: poll.allowMultiple === true,
    mappedTo: "list",
  };
}

export function parseOutboundPollDisplay(
  metadata: Record<string, unknown> | undefined | null,
): WhatsAppPollDisplay | null {
  const fabInteractive = parseFabInteractive(metadata);
  if (fabInteractive?.kind === "poll" && fabInteractive.poll) {
    return {
      question: fabInteractive.poll.question,
      options: fabInteractive.poll.options.map((title, i) => ({
        id: slugifyInteractiveId(title, i, "row"),
        title,
      })),
      allowMultiple: fabInteractive.poll.allowMultiple,
    };
  }

  const fabPoll = parseFabPoll(metadata);
  if (!fabPoll) return null;

  const interactive = asRecord(metadata?.interactive);
  const action = asRecord(interactive?.action);
  const sections = Array.isArray(action?.sections) ? action.sections : [];
  const listRows = sections
    .flatMap((section) => {
      const rows = Array.isArray(asRecord(section)?.rows) ? asRecord(section)!.rows! : [];
      return rows;
    })
    .map((row, i) => {
      const node = asRecord(row);
      const title = String(node?.title ?? fabPoll.options[i] ?? "").trim();
      const id = String(node?.id ?? "").trim();
      if (!title) return null;
      return { id: id || title, title };
    })
    .filter(Boolean) as { id: string; title: string }[];

  const options =
    listRows.length >= 2
      ? listRows
      : fabPoll.options.map((title, i) => ({
          id: slugifyInteractiveId(title, i, "row"),
          title,
        }));

  if (options.length < 2) return null;

  return {
    question: fabPoll.question,
    options,
    allowMultiple: fabPoll.allowMultiple,
  };
}

/** Enquete recebida do app WhatsApp do cliente — Cloud API retorna type unsupported (131051). */
export function isUnsupportedInboundPoll(
  metadata: Record<string, unknown> | undefined | null,
): boolean {
  if (!metadata) return false;
  const type = String(metadata.type ?? metadata.message_type ?? "");
  if (type !== "unsupported") return false;

  const errors = metadata.errors;
  if (!Array.isArray(errors)) return false;

  return errors.some((err) => {
    const node = asRecord(err);
    const code = node?.code;
    return code === 131051 || code === "131051";
  });
}

export function parseOutboundCarouselDisplay(
  metadata: Record<string, unknown> | undefined | null,
): WhatsAppCarouselDisplay | null {
  const fabInteractive = parseFabInteractive(metadata);
  if (!fabInteractive || fabInteractive.kind !== "carousel" || !fabInteractive.carousel?.cards?.length) {
    return null;
  }

  return {
    bodyText: fabInteractive.bodyText,
    cards: fabInteractive.carousel.cards.map((card) => ({
      text: card.text,
      imageUrl: card.image,
      buttons: (card.buttons ?? []).map((b) => ({
        id: b.id,
        text: b.text,
        type: b.type,
      })),
    })),
  };
}

export function parseOutboundInteractive(
  metadata: Record<string, unknown> | undefined | null,
): WhatsAppInteractiveDisplay | null {
  if (!metadata) return null;

  const fabInteractive = parseFabInteractive(metadata);
  if (fabInteractive) {
    if (fabInteractive.kind === "poll" || fabInteractive.kind === "carousel") return null;
    const display = fabInteractiveToDisplay(fabInteractive);
    if (display && "type" in display) return display;
  }

  if (parseFabPoll(metadata)) return null;

  const interactive = asRecord(metadata.interactive);
  if (!interactive) return null;

  const type = interactive.type;
  if (type !== "button" && type !== "list") return null;

  const bodyText = String(asRecord(interactive.body)?.text ?? "").trim();
  if (!bodyText) return null;

  if (type === "button") {
    const action = asRecord(interactive.action);
    const rawButtons = Array.isArray(action?.buttons) ? action.buttons : [];
    const buttons = rawButtons
      .map((btn) => {
        const reply = asRecord(asRecord(btn)?.reply);
        const title = String(reply?.title ?? "").trim();
        const id = String(reply?.id ?? "").trim();
        if (!title) return null;
        return { id: id || title, title };
      })
      .filter(Boolean) as WhatsAppInteractiveButton[];

    if (buttons.length === 0) return null;
    return { type: "button", bodyText, buttons };
  }

  const action = asRecord(interactive.action);
  const menuButton = String(action?.button ?? "").trim();
  const sections = Array.isArray(action?.sections) ? action.sections : [];
  const listRows = sections
    .flatMap((section) => {
      const rows = Array.isArray(asRecord(section)?.rows) ? asRecord(section)!.rows! : [];
      return rows;
    })
    .map((row) => {
      const node = asRecord(row);
      const title = String(node?.title ?? "").trim();
      const id = String(node?.id ?? "").trim();
      const description = String(node?.description ?? "").trim();
      if (!title) return null;
      return {
        id: id || title,
        title,
        ...(description ? { description } : {}),
      };
    })
    .filter(Boolean) as WhatsAppInteractiveListRow[];

  if (!menuButton && listRows.length === 0) return null;
  return { type: "list", bodyText, menuButton: menuButton || "Ver opções", listRows };
}

export function parseInboundInteractiveReply(
  metadata: Record<string, unknown> | undefined | null,
): WhatsAppInteractiveInboundReply | null {
  if (!metadata) return null;

  const fabNode = asRecord(metadata[FAB_INTERACTIVE_METADATA_KEY]);
  if (fabNode?.kind === "button_reply" || fabNode?.kind === "list_reply" || fabNode?.kind === "poll_vote") {
    const reply = asRecord(fabNode.reply);
    const title = String(reply?.title ?? "").trim();
    if (!title) return null;
    const replyKind = String(reply?.kind ?? "");
    if (replyKind === "poll") {
      return { kind: "list", title, id: undefined, description: undefined };
    }
    return {
      kind: replyKind === "list" ? "list" : "button",
      title,
      id: reply?.id ? String(reply.id).trim() : undefined,
      description: reply?.description ? String(reply.description).trim() : undefined,
    };
  }

  const messageType = metadata.message_type ?? metadata.type;
  if (messageType !== "interactive" && messageType !== "button") return null;

  const interactive = asRecord(metadata.interactive);
  const buttonReply = asRecord(interactive?.button_reply);
  const listReply = asRecord(interactive?.list_reply);

  if (buttonReply) {
    const title = String(buttonReply.title ?? buttonReply.id ?? "").trim();
    if (!title) return null;
    return {
      kind: "button",
      title,
      id: String(buttonReply.id ?? "").trim() || undefined,
    };
  }

  if (listReply) {
    const title = String(listReply.title ?? listReply.id ?? "").trim();
    if (!title) return null;
    const description = String(listReply.description ?? "").trim();
    return {
      kind: "list",
      title,
      description: description || undefined,
      id: String(listReply.id ?? "").trim() || undefined,
    };
  }

  if (messageType === "button") {
    const button = asRecord(metadata.button);
    const title = String(button?.text ?? "").trim();
    if (!title) return null;
    return { kind: "button", title };
  }

  return null;
}

/** Recupera preview a partir do body legado `[Botões: ...]` quando metadata não tem interactive. */
export function parseInteractivePreviewFromBody(content: string): WhatsAppInteractiveDisplay | null {
  const stripped = content.replace(PREVIEW_SUFFIX_RE, "");
  const suffixMatch = content.match(PREVIEW_SUFFIX_RE);
  if (!suffixMatch) return null;

  const bodyText = stripped.trim();
  const buttonsMatch = content.match(/\[Botões:\s*([^\]]+)\]/);
  if (buttonsMatch) {
    const buttons = buttonsMatch[1]
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((title, i) => ({ id: slugifyInteractiveId(title, i, "btn"), title }));
    if (buttons.length === 0) return null;
    return { type: "button", bodyText, buttons };
  }

  const menuMatch = content.match(/\[Menu:\s*([^\]]+)\]/);
  const optionsMatch = content.match(/\[Opções:\s*([^\]]+)\]/);
  const menuButton = menuMatch?.[1]?.trim() || "Ver opções";
  const listRows =
    optionsMatch?.[1]
      ?.split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 10)
      .map((title, i) => ({ id: slugifyInteractiveId(title, i, "row"), title })) ?? [];

  if (!bodyText && !menuButton && listRows.length === 0) return null;
  return { type: "list", bodyText, menuButton, listRows };
}

export function isInteractivePlaceholderContent(
  content: string,
  metadata: Record<string, unknown> | undefined | null,
): boolean {
  if (parseOutboundCarouselDisplay(metadata)) return true;
  if (parseFabInteractive(metadata)) return true;
  if (parseOutboundPollDisplay(metadata)) return true;
  if (parseOutboundInteractive(metadata)) return true;
  if (parseInboundInteractiveReply(metadata)) return false;
  return PREVIEW_SUFFIX_RE.test(content);
}

export function buildFabPollListPayload(
  question: string,
  optionTitles: string[],
): {
  interactive: { type: "list"; body: { text: string }; action: Record<string, unknown> };
  fab_poll: FabPollMetadata;
  fab_interactive: FabInteractiveCanonical;
} {
  const trimmedQuestion = question.trim();
  const options = optionTitles.map((t) => t.trim()).filter(Boolean).slice(0, 10);
  const rows = options.map((title, i) => ({
    id: slugifyInteractiveId(title, i, "row"),
    title,
  }));

  const fabInteractive = buildFabInteractiveFromPollForm(trimmedQuestion, options);

  return {
    interactive: {
      type: "list",
      body: { text: trimmedQuestion },
      action: {
        button: FAB_POLL_MENU_BUTTON,
        sections: [{ title: "Opções", rows }],
      },
    },
    fab_poll: {
      question: trimmedQuestion,
      options: options,
      allowMultiple: false,
      mappedTo: "list",
    },
    fab_interactive: fabInteractive,
  };
}
